import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';;
import { getToken } from '../auth';
import { deferAfterPaint } from '../lib/deferAfterPaint';
import { useAuth } from './AuthContext';

export type NavCounts = {
  products: number;
  stockMovements: number;
  orders: number;
  customers: number;
  documents: number;
  purchaseOrders: number;
  suppliers: number;
  users: number;
  b2bClients: number;
  b2bSalesOrders: number;
  leads: number;
  leadFollowUps: number;
  supportTickets: number;
  productReviewsPending: number;
  messagesUnread: number;
};

const EMPTY: NavCounts = {
  products: 0,
  stockMovements: 0,
  orders: 0,
  customers: 0,
  documents: 0,
  purchaseOrders: 0,
  suppliers: 0,
  users: 0,
  b2bClients: 0,
  b2bSalesOrders: 0,
  leads: 0,
  leadFollowUps: 0,
  supportTickets: 0,
  productReviewsPending: 0,
  messagesUnread: 0,
};

const NavCountsCtx = createContext<NavCounts>(EMPTY);

export function NavCountsProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [counts, setCounts] = useState<NavCounts>(EMPTY);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loadInFlightRef = useRef<Promise<void> | null>(null);

  const load = useCallback(async () => {
    if (loadInFlightRef.current) {
      return loadInFlightRef.current;
    }

    const task = (async () => {
      try {
        const token = getToken();
        if (!token || !user) {
          setCounts(EMPTY);
          return;
        }
        const [navRes, messagesRes] = await Promise.all([
          apiGet<{ data: Omit<NavCounts, 'messagesUnread'> }>('/reports/nav-counts', token),
          apiGet<{ data: { count: number } }>('/conversations/unread-count', token).catch(
            () => ({ data: { count: 0 } }),
          ),
        ]);
        const base = unwrapData<Omit<NavCounts, 'messagesUnread'>>(navRes);
        setCounts({
          ...base,
          messagesUnread: messagesRes.data?.count ?? 0,
        });
      } catch {
        // silent
      }
    })();

    loadInFlightRef.current = task;
    try {
      await task;
    } finally {
      if (loadInFlightRef.current === task) {
        loadInFlightRef.current = null;
      }
    }
  }, [user]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (authLoading || !user) {
      setCounts(EMPTY);
      return;
    }

    const cancelDefer = deferAfterPaint(() => {
      void load();
      intervalRef.current = setInterval(() => {
        if (typeof document !== 'undefined' && document.hidden) return;
        void load();
      }, 120_000);
    });

    return () => {
      cancelDefer();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [authLoading, user, load]);

  return <NavCountsCtx.Provider value={counts}>{children}</NavCountsCtx.Provider>;
}

export function useNavCounts() {
  return useContext(NavCountsCtx);
}
